/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * AddonsMarketplace — Shared Component
 * ============================================================================
 *
 * Reusable add-on marketplace for org admins. Displays available feature
 * add-ons grouped by module, with purchase/cancel flows.
 *
 * Used in:
 *  - BillingPage (Add-Ons tab)
 *  - SubscriptionManagementPage (section)
 *  - Standalone /add-ons route
 *
 * ============================================================================
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Package,
  Loader2,
  ShoppingCart,
  X,
  Star,
  Zap,
  AlertCircle,
  FileText,
  Sparkles,
  Minus,
  Plus,
  Users,
  ArrowUpRight,
  Shield,
  Crown,
} from 'lucide-react';
import {
  useActiveAddons,
  useAvailableAddons,
  usePurchaseAddon,
  useCancelAddon,
  useUpdateAddonUserCount,
} from '@/hooks/api/useAddons';
import { useMySubscription } from '@/hooks/api/useMySubscription';
import { useNotification } from '@/hooks/useNotification';
import { formatErrorForDisplay } from '@/services/api/errors';
import type { FeatureAddon, FeatureAddonItem } from '@/types/addon.types';

/* ============================== Types ============================== */

interface ModalState {
  type: 'purchase' | 'cancel' | 'upgrade' | null;
  addon: FeatureAddon | null;
  activeItem?: FeatureAddonItem;
  currentTier?: string;
}

/* ============================== Tier Hierarchy ============================== */

const TIER_ORDER = ['INCLUDED', 'STARTER', 'BASIC', 'PRO', 'ENTERPRISE'];

function tierRank(tierCode: string): number {
  const idx = TIER_ORDER.indexOf(tierCode.toUpperCase());
  return idx >= 0 ? idx : -1;
}

function isTierSufficient(currentTier: string, requiredTier: string): boolean {
  return tierRank(currentTier) >= tierRank(requiredTier);
}

/* ============================== Helpers ============================== */

function formatPrice(
  addon: FeatureAddon,
  billingCycle: 'MONTHLY' | 'ANNUAL',
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (addon.pricingModel === 'FLAT') {
    if (billingCycle === 'ANNUAL' && addon.annualPriceUsd != null) {
      return `${t('addons.marketplace.currencyValue', { amount: addon.annualPriceUsd.toFixed(2) })}${t('addons.marketplace.perYear')}`;
    }
    if (addon.monthlyPriceUsd != null) {
      return `${t('addons.marketplace.currencyValue', { amount: addon.monthlyPriceUsd.toFixed(2) })}${t('addons.marketplace.perMonth')}`;
    }
  }

  if (addon.pricingModel === 'PER_USER') {
    if (billingCycle === 'ANNUAL' && addon.perUserAnnualUsd != null) {
      return `${t('addons.marketplace.currencyValue', { amount: addon.perUserAnnualUsd.toFixed(2) })}${t('addons.marketplace.perUserYear')}`;
    }
    if (addon.perUserMonthlyUsd != null) {
      return `${t('addons.marketplace.currencyValue', { amount: addon.perUserMonthlyUsd.toFixed(2) })}${t('addons.marketplace.perUserMonth')}`;
    }
  }

  return t('addons.marketplace.currencyValue', { amount: '0.00' });
}

function isPremiumTemplate(addon: FeatureAddon): boolean {
  return addon.featureCode.startsWith('PREMIUM_TEMPLATE_');
}

/** Resolve translated name/description for an addon, falling back to DB value. */
function addonName(addon: FeatureAddon, t: (key: string) => string): string {
  const key = `addons.catalog.${addon.moduleCode}.${addon.featureCode}.name`;
  const translated = t(key);
  return translated !== key ? translated : addon.name;
}

function addonDescription(addon: FeatureAddon, t: (key: string) => string): string {
  if (!addon.description) return '';
  const key = `addons.catalog.${addon.moduleCode}.${addon.featureCode}.description`;
  const translated = t(key);
  return translated !== key ? translated : addon.description;
}

/**
 * Safely render i18n strings that contain trusted HTML (e.g. <strong> tags).
 * The translation values come from our own JSON files, not from user input.
 */
function renderTranslatedHtml(html: string): React.ReactNode {
  // Only allow <strong> and </strong> tags from our translation strings
  const parts = html.split(/(<strong>|<\/strong>)/);
  const result: React.ReactNode[] = [];
  let bold = false;

  for (let i = 0; i < parts.length; i++) {
    if (parts[i] === '<strong>') {
      bold = true;
    } else if (parts[i] === '</strong>') {
      bold = false;
    } else if (parts[i]) {
      result.push(
        bold ? <strong key={i}>{parts[i]}</strong> : <React.Fragment key={i}>{parts[i]}</React.Fragment>,
      );
    }
  }
  return result;
}

/* ============================== AddonCard ============================== */

interface AddonCardProps {
  addon: FeatureAddon;
  activeItem?: FeatureAddonItem;
  billingCycle: 'MONTHLY' | 'ANNUAL';
  index: number;
  large?: boolean;
  onPurchase: (addon: FeatureAddon) => void;
  onCancel: (addon: FeatureAddon, activeItem: FeatureAddonItem) => void;
  onUpdateUsers: (addon: FeatureAddon, activeItem: FeatureAddonItem, delta: number) => void;
  isUpdatingUsers?: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function AddonCard({
  addon,
  activeItem,
  billingCycle,
  index,
  large,
  onPurchase,
  onCancel,
  onUpdateUsers,
  isUpdatingUsers,
  t,
}: AddonCardProps) {
  const isActive = activeItem?.status === 'ACTIVE';
  const isTrial = activeItem?.status === 'TRIAL';
  const isPurchased = isActive || isTrial;
  const isPerUser = addon.pricingModel === 'PER_USER';

  return (
    <div
      className={`addons-mp-card${large ? ' addons-mp-card--large' : ''}`}
      style={{ animationDelay: `${0.06 * index}s` }}
    >
      <div className="addons-mp-card-header">
        <div className="addons-mp-card-title-row">
          <span className="addons-mp-card-icon">
            {isPremiumTemplate(addon) ? <FileText size={18} /> : <Zap size={18} />}
          </span>
          <h4 className="addons-mp-card-name">{addonName(addon, t)}</h4>
        </div>
        {addon.description && (
          <p className="addons-mp-card-desc">{addonDescription(addon, t)}</p>
        )}
      </div>

      <div className="addons-mp-card-body">
        {/* Pricing */}
        <div className="addons-mp-card-price">
          {formatPrice(addon, billingCycle, t)}
        </div>
        <div className="addons-mp-card-pricing-type">
          {addon.pricingModel === 'FLAT'
            ? t('addons.marketplace.flatRate')
            : t('addons.marketplace.perUserRate')}
        </div>

        {/* Badges */}
        <div className="addons-mp-card-badges">
          {isActive && (
            <span className="addons-mp-badge addons-mp-badge--active">
              <Star size={12} />
              {t('addons.marketplace.active')}
            </span>
          )}
          {isTrial && (
            <span className="addons-mp-badge addons-mp-badge--trial">
              <Sparkles size={12} />
              {t('addons.marketplace.trial')}
              {activeItem?.trialDaysRemaining != null && (
                <> &mdash; {t('addons.marketplace.trialDaysLeft', { days: activeItem.trialDaysRemaining })}</>
              )}
            </span>
          )}
          {!isPurchased && addon.trialDays > 0 && (
            <span className="addons-mp-badge addons-mp-badge--trial-offer">
              {t('addons.marketplace.freeTrial', { days: addon.trialDays })}
            </span>
          )}
        </div>

        {/* User count stepper (PER_USER add-ons only, when purchased) */}
        {isPerUser && isPurchased && activeItem && (
          <div className="addons-mp-user-stepper">
            <div className="addons-mp-user-stepper-label">
              <Users size={13} />
              {t('addons.marketplace.users')}
            </div>
            <div className="addons-mp-user-stepper-controls">
              <button
                className="addons-mp-stepper-btn"
                onClick={() => onUpdateUsers(addon, activeItem, -1)}
                disabled={activeItem.userCount <= 1 || isUpdatingUsers}
                aria-label="Decrease users"
              >
                <Minus size={14} />
              </button>
              <span className="addons-mp-user-count">{activeItem.userCount}</span>
              <button
                className="addons-mp-stepper-btn"
                onClick={() => onUpdateUsers(addon, activeItem, 1)}
                disabled={isUpdatingUsers}
                aria-label="Increase users"
              >
                <Plus size={14} />
              </button>
            </div>
            <div className="addons-mp-user-total">
              {t('addons.marketplace.currencyValue', { amount: activeItem.lineTotalUsd?.toFixed(2) })}{billingCycle === 'ANNUAL' ? t('addons.marketplace.perYear') : t('addons.marketplace.perMonth')}
            </div>
          </div>
        )}

        {/* Min tier info */}
        {addon.minTierCode && (
          <div className="addons-mp-card-tier-info">
            <AlertCircle size={13} />
            {t('addons.marketplace.requiresTier', { tier: addon.minTierCode })}
          </div>
        )}
      </div>

      <div className="addons-mp-card-footer">
        {isPurchased && activeItem ? (
          <button
            className="addons-mp-btn addons-mp-btn--cancel"
            onClick={() => onCancel(addon, activeItem)}
          >
            {t('addons.marketplace.cancelAddon')}
          </button>
        ) : (
          <button
            className="addons-mp-btn addons-mp-btn--purchase"
            onClick={() => onPurchase(addon)}
          >
            <ShoppingCart size={15} />
            {t('addons.marketplace.purchase')}
          </button>
        )}
      </div>
    </div>
  );
}

/* ============================== ModuleAddonsSection ============================== */

interface ModuleAddonsSectionProps {
  moduleCode: string;
  moduleName?: string;
  activeItems: FeatureAddonItem[];
  billingCycle: 'MONTHLY' | 'ANNUAL';
  onPurchase: (addon: FeatureAddon) => void;
  onCancel: (addon: FeatureAddon, activeItem: FeatureAddonItem) => void;
  onUpdateUsers: (addon: FeatureAddon, activeItem: FeatureAddonItem, delta: number) => void;
  isUpdatingUsers?: boolean;
  /** Collect premium template add-ons for rendering separately */
  onCollectTemplates: (addons: FeatureAddon[]) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function ModuleAddonsSection({
  moduleCode,
  moduleName,
  activeItems,
  billingCycle,
  onPurchase,
  onCancel,
  onUpdateUsers,
  isUpdatingUsers,
  onCollectTemplates,
  t,
}: ModuleAddonsSectionProps) {
  const { data: available, isLoading } = useAvailableAddons(moduleCode);

  // Separate standard add-ons from premium templates
  const standardAddons = useMemo(
    () => (available ?? []).filter((a) => !isPremiumTemplate(a)),
    [available],
  );

  const templateAddons = useMemo(
    () => (available ?? []).filter((a) => isPremiumTemplate(a)),
    [available],
  );

  // Propagate templates up for the separate section
  React.useEffect(() => {
    if (templateAddons.length > 0) {
      onCollectTemplates(templateAddons);
    }
  }, [templateAddons, onCollectTemplates]);

  if (isLoading) {
    return (
      <div className="addons-mp-section-loading">
        <Loader2 size={18} className="addons-mp-spin" />
      </div>
    );
  }

  if (standardAddons.length === 0) return null;

  return (
    <div className="addons-mp-section">
      <h3 className="addons-mp-section-title">
        <Package size={18} />
        {moduleName ?? moduleCode}
      </h3>
      <div className="addons-mp-grid">
        {standardAddons.map((addon, i) => {
          const activeItem = activeItems.find(
            (ai) => ai.moduleCode === addon.moduleCode && ai.featureCode === addon.featureCode,
          );
          return (
            <AddonCard
              key={addon.id}
              addon={addon}
              activeItem={activeItem}
              billingCycle={billingCycle}
              index={i}
              onPurchase={onPurchase}
              onCancel={onCancel}
              onUpdateUsers={onUpdateUsers}
              isUpdatingUsers={isUpdatingUsers}
              t={t}
            />
          );
        })}
      </div>
    </div>
  );
}

/* ============================== PurchaseModal ============================== */

interface PurchaseModalProps {
  addon: FeatureAddon;
  billingCycle: 'MONTHLY' | 'ANNUAL';
  isSubmitting: boolean;
  onConfirm: () => void;
  onClose: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function PurchaseModal({ addon, billingCycle, isSubmitting, onConfirm, onClose, t }: PurchaseModalProps) {
  const price = formatPrice(addon, billingCycle, t);
  const displayName = addonName(addon, t);

  return (
    <div className="addons-mp-overlay" onClick={onClose}>
      <div className="addons-mp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="addons-mp-modal-header">
          <h3>{t('addons.marketplace.purchaseConfirm.title')}</h3>
          <button className="addons-mp-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="addons-mp-modal-body">
          <p>
            {renderTranslatedHtml(
              t('addons.marketplace.purchaseConfirm.message', {
                name: displayName,
                price,
                interpolation: { escapeValue: false },
              }) as string,
            )}
          </p>
          {addon.trialDays > 0 && (
            <p className="addons-mp-modal-trial">
              {t('addons.marketplace.purchaseConfirm.trialMessage', {
                days: addon.trialDays,
              })}
            </p>
          )}
        </div>
        <div className="addons-mp-modal-footer">
          <button className="addons-mp-btn addons-mp-btn--ghost" onClick={onClose} disabled={isSubmitting}>
            {t('addons.marketplace.purchaseConfirm.cancel')}
          </button>
          <button
            className="addons-mp-btn addons-mp-btn--purchase addons-mp-btn--modal"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 size={15} className="addons-mp-spin" />}
            {t('addons.marketplace.purchaseConfirm.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================== CancelModal ============================== */

interface CancelModalProps {
  addon: FeatureAddon;
  isSubmitting: boolean;
  onConfirm: (reason?: string) => void;
  onClose: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function CancelModal({ addon, isSubmitting, onConfirm, onClose, t }: CancelModalProps) {
  const [reason, setReason] = useState('');
  const displayName = addonName(addon, t);

  return (
    <div className="addons-mp-overlay" onClick={onClose}>
      <div className="addons-mp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="addons-mp-modal-header">
          <h3>{t('addons.marketplace.cancelConfirm.title')}</h3>
          <button className="addons-mp-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="addons-mp-modal-body">
          <p className="addons-mp-modal-warning">
            {renderTranslatedHtml(
              t('addons.marketplace.cancelConfirm.message', {
                name: displayName,
                interpolation: { escapeValue: false },
              }) as string,
            )}
          </p>
          <label className="addons-mp-modal-label">
            {t('addons.marketplace.cancelConfirm.reasonLabel')}
          </label>
          <textarea
            className="addons-mp-modal-textarea"
            placeholder={t('addons.marketplace.cancelConfirm.reasonPlaceholder') as string}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
        </div>
        <div className="addons-mp-modal-footer">
          <button className="addons-mp-btn addons-mp-btn--ghost" onClick={onClose} disabled={isSubmitting}>
            {t('addons.marketplace.cancelConfirm.cancel')}
          </button>
          <button
            className="addons-mp-btn addons-mp-btn--danger"
            onClick={() => onConfirm(reason || undefined)}
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 size={15} className="addons-mp-spin" />}
            {t('addons.marketplace.cancelConfirm.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================== UpgradeModal ============================== */

interface UpgradeModalProps {
  addon: FeatureAddon;
  currentTier: string;
  onClose: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function UpgradeModal({ addon, currentTier, onClose, t }: UpgradeModalProps) {
  const displayName = addonName(addon, t);
  const requiredTier = addon.minTierCode ?? '';

  const tierLabel = (tier: string) => {
    const key = `addons.marketplace.upgradeRequired.tiers.${tier}`;
    const translated = t(key);
    return translated !== key ? translated : tier;
  };

  return (
    <div className="addons-mp-overlay" onClick={onClose}>
      <div className="addons-mp-modal addons-mp-upgrade-modal" onClick={(e) => e.stopPropagation()}>
        {/* Decorative top accent */}
        <div className="addons-mp-upgrade-accent" />

        <div className="addons-mp-modal-header addons-mp-upgrade-header">
          <div className="addons-mp-upgrade-header-left">
            <div className="addons-mp-upgrade-icon-wrap">
              <Crown size={20} />
            </div>
            <h3>{t('addons.marketplace.upgradeRequired.title')}</h3>
          </div>
          <button className="addons-mp-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="addons-mp-modal-body addons-mp-upgrade-body">
          <p className="addons-mp-upgrade-message">
            {renderTranslatedHtml(
              t('addons.marketplace.upgradeRequired.message', {
                name: displayName,
                interpolation: { escapeValue: false },
              }) as string,
            )}
          </p>

          {/* Tier comparison */}
          <div className="addons-mp-upgrade-tiers">
            <div className="addons-mp-upgrade-tier addons-mp-upgrade-tier--current">
              <div className="addons-mp-upgrade-tier-label">
                {t('addons.marketplace.upgradeRequired.currentPlan')}
              </div>
              <div className="addons-mp-upgrade-tier-badge">
                <Shield size={14} />
                {tierLabel(currentTier)}
              </div>
            </div>
            <div className="addons-mp-upgrade-tier-arrow">
              <ArrowUpRight size={18} />
            </div>
            <div className="addons-mp-upgrade-tier addons-mp-upgrade-tier--required">
              <div className="addons-mp-upgrade-tier-label">
                {t('addons.marketplace.upgradeRequired.requiredPlan')}
              </div>
              <div className="addons-mp-upgrade-tier-badge addons-mp-upgrade-tier-badge--required">
                <Crown size={14} />
                {tierLabel(requiredTier)}
              </div>
            </div>
          </div>

          <p className="addons-mp-upgrade-hint">
            {t('addons.marketplace.upgradeRequired.hint')}
          </p>
        </div>

        <div className="addons-mp-modal-footer">
          <button className="addons-mp-btn addons-mp-btn--ghost" onClick={onClose}>
            {t('addons.marketplace.upgradeRequired.dismiss')}
          </button>
          <a
            href="/billing/subscription"
            className="addons-mp-btn addons-mp-btn--upgrade"
            onClick={onClose}
          >
            <ArrowUpRight size={15} />
            {t('addons.marketplace.upgradeRequired.upgradeCta')}
          </a>
        </div>
      </div>
    </div>
  );
}

/* ============================== AddonsMarketplace (Main) ============================== */

function AddonsMarketplace() {
  const { t } = useTranslation('platform');

  const { data: subscription, isLoading: subLoading } = useMySubscription();
  const { data: activeItems = [], isLoading: activeLoading } = useActiveAddons();

  const purchaseMutation = usePurchaseAddon();
  const cancelMutation = useCancelAddon();
  const userCountMutation = useUpdateAddonUserCount();
  const notification = useNotification();

  const [modal, setModal] = useState<ModalState>({ type: null, addon: null });
  const [templateAddons, setTemplateAddons] = useState<FeatureAddon[]>([]);

  const billingCycle = subscription?.billingCycle ?? 'MONTHLY';

  // Unique subscribed modules
  const subscribedModules = useMemo(() => {
    if (!subscription?.modules) return [];
    const seen = new Set<string>();
    return subscription.modules.filter((m) => {
      if (seen.has(m.moduleCode)) return false;
      seen.add(m.moduleCode);
      return true;
    });
  }, [subscription?.modules]);

  // Template collector — uses a stable callback to avoid re-render loops
  const handleCollectTemplates = useCallback((addons: FeatureAddon[]) => {
    setTemplateAddons((prev) => {
      // Merge, deduplicate by id
      const ids = new Set(prev.map((a) => a.id));
      const newOnes = addons.filter((a) => !ids.has(a.id));
      return newOnes.length > 0 ? [...prev, ...newOnes] : prev;
    });
  }, []);

  // Build a map of moduleCode → current tierCode from the subscription
  const moduleTierMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (subscription?.modules) {
      for (const mod of subscription.modules) {
        if (!map[mod.moduleCode]) {
          map[mod.moduleCode] = mod.tierCode;
        }
      }
    }
    return map;
  }, [subscription?.modules]);

  // Modal handlers
  const openPurchase = useCallback((addon: FeatureAddon) => {
    // Check tier requirement before opening purchase modal
    if (addon.minTierCode) {
      const currentTier = moduleTierMap[addon.moduleCode];
      if (currentTier && !isTierSufficient(currentTier, addon.minTierCode)) {
        setModal({ type: 'upgrade', addon, currentTier });
        return;
      }
    }
    setModal({ type: 'purchase', addon });
  }, [moduleTierMap]);

  const openCancel = useCallback((addon: FeatureAddon, activeItem: FeatureAddonItem) => {
    setModal({ type: 'cancel', addon, activeItem });
  }, []);

  const closeModal = useCallback(() => {
    setModal({ type: null, addon: null });
  }, []);

  const handleUpdateUsers = useCallback(
    (_addon: FeatureAddon, activeItem: FeatureAddonItem, delta: number) => {
      const newCount = activeItem.userCount + delta;
      if (newCount < 1) return;
      userCountMutation.mutate(
        { moduleCode: activeItem.moduleCode, featureCode: activeItem.featureCode, userCount: newCount },
        {
          onError: (err) => {
            const { message } = formatErrorForDisplay(err);
            notification.error(message);
          },
        },
      );
    },
    [userCountMutation, notification],
  );

  const handlePurchase = useCallback(() => {
    if (!modal.addon) return;
    const { moduleCode, featureCode } = modal.addon;
    const displayName = addonName(modal.addon, t);
    purchaseMutation.mutate(
      { moduleCode, featureCode },
      {
        onSuccess: () => {
          notification.success(
            t('addons.marketplace.purchaseSuccess', { name: displayName }),
          );
          closeModal();
        },
        onError: (err) => {
          const { message } = formatErrorForDisplay(err);
          notification.error(message);
        },
      },
    );
  }, [modal.addon, purchaseMutation, notification, t, closeModal]);

  const handleCancel = useCallback(
    (reason?: string) => {
      if (!modal.addon) return;
      const { moduleCode, featureCode } = modal.addon;
      const displayName = addonName(modal.addon, t);
      cancelMutation.mutate(
        { moduleCode, featureCode, reason },
        {
          onSuccess: () => {
            notification.success(
              t('addons.marketplace.cancelSuccess', { name: displayName }),
            );
            closeModal();
          },
          onError: (err) => {
            const { message } = formatErrorForDisplay(err);
            notification.error(message);
          },
        },
      );
    },
    [modal.addon, cancelMutation, notification, t, closeModal],
  );

  const handleBuyAllTemplates = useCallback(() => {
    const unpurchased = templateAddons.filter(
      (a) => !activeItems.find((ai) => ai.moduleCode === a.moduleCode && ai.featureCode === a.featureCode),
    );
    if (unpurchased.length === 0) return;

    unpurchased.forEach((addon) => {
      purchaseMutation.mutate(
        { moduleCode: addon.moduleCode, featureCode: addon.featureCode },
        {
          onSuccess: () => {
            notification.success(
              t('addons.marketplace.purchaseSuccess', { name: addonName(addon, t) }),
            );
          },
          onError: (err) => {
            const { message } = formatErrorForDisplay(err);
            notification.error(message);
          },
        },
      );
    });
  }, [templateAddons, activeItems, purchaseMutation, notification, t]);

  /* -------------- Loading -------------- */

  if (subLoading || activeLoading) {
    return (
      <div className="addons-mp">
        <div className="addons-mp-loading">
          <Loader2 size={28} className="addons-mp-spin" />
        </div>
        <style>{STYLES}</style>
      </div>
    );
  }

  /* -------------- No Modules -------------- */

  if (subscribedModules.length === 0) {
    return (
      <div className="addons-mp">
        <div className="addons-mp-empty">
          <Package size={40} />
          <p>{t('addons.marketplace.noAddons')}</p>
        </div>
        <style>{STYLES}</style>
      </div>
    );
  }

  /* -------------- Render -------------- */

  const unpurchasedTemplates = templateAddons.filter(
    (a) => !activeItems.find((ai) => ai.moduleCode === a.moduleCode && ai.featureCode === a.featureCode),
  );

  return (
    <div className="addons-mp">
      {/* Header */}
      <div className="addons-mp-header">
        <div>
          <h2 className="addons-mp-title">{t('addons.marketplace.title')}</h2>
          <p className="addons-mp-subtitle">{t('addons.marketplace.subtitle')}</p>
        </div>
      </div>

      {/* Per-module sections */}
      {subscribedModules.map((mod) => (
        <ModuleAddonsSection
          key={mod.moduleCode}
          moduleCode={mod.moduleCode}
          moduleName={mod.moduleName ?? mod.moduleCode}
          activeItems={activeItems}
          billingCycle={billingCycle}
          onPurchase={openPurchase}
          onCancel={openCancel}
          onUpdateUsers={handleUpdateUsers}
          isUpdatingUsers={userCountMutation.isPending}
          onCollectTemplates={handleCollectTemplates}
          t={t}
        />
      ))}

      {/* Premium Templates Section */}
      {templateAddons.length > 0 && (
        <div className="addons-mp-section addons-mp-templates-section">
          <div className="addons-mp-templates-header">
            <div>
              <h3 className="addons-mp-section-title">
                <Sparkles size={18} />
                {t('addons.marketplace.premiumTemplates.title')}
              </h3>
              <p className="addons-mp-templates-subtitle">
                {t('addons.marketplace.premiumTemplates.subtitle')}
              </p>
            </div>
            {unpurchasedTemplates.length > 1 && (
              <button
                className="addons-mp-btn addons-mp-btn--purchase addons-mp-btn--modal"
                onClick={handleBuyAllTemplates}
                disabled={purchaseMutation.isPending}
              >
                {purchaseMutation.isPending && <Loader2 size={15} className="addons-mp-spin" />}
                {t('addons.marketplace.premiumTemplates.buyAll')}
              </button>
            )}
          </div>
          <div className="addons-mp-grid addons-mp-grid--templates">
            {templateAddons.map((addon, i) => {
              const activeItem = activeItems.find(
                (ai) => ai.moduleCode === addon.moduleCode && ai.featureCode === addon.featureCode,
              );
              return (
                <AddonCard
                  key={addon.id}
                  addon={addon}
                  activeItem={activeItem}
                  billingCycle={billingCycle}
                  index={i}
                  large
                  onPurchase={openPurchase}
                  onCancel={openCancel}
                  onUpdateUsers={handleUpdateUsers}
                  isUpdatingUsers={userCountMutation.isPending}
                  t={t}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Purchase Modal */}
      {modal.type === 'purchase' && modal.addon && (
        <PurchaseModal
          addon={modal.addon}
          billingCycle={billingCycle}
          isSubmitting={purchaseMutation.isPending}
          onConfirm={handlePurchase}
          onClose={closeModal}
          t={t}
        />
      )}

      {/* Cancel Modal */}
      {modal.type === 'cancel' && modal.addon && (
        <CancelModal
          addon={modal.addon}
          isSubmitting={cancelMutation.isPending}
          onConfirm={handleCancel}
          onClose={closeModal}
          t={t}
        />
      )}

      {/* Upgrade Required Modal */}
      {modal.type === 'upgrade' && modal.addon && modal.currentTier && (
        <UpgradeModal
          addon={modal.addon}
          currentTier={modal.currentTier}
          onClose={closeModal}
          t={t}
        />
      )}

      <style>{STYLES}</style>
    </div>
  );
}

/* ============================== Styles ============================== */

const STYLES = `
/* ===== Layout ===== */
.addons-mp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  box-sizing: border-box;
}

.addons-mp-header {
  animation: addons-mp-header-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.addons-mp-title {
  font-family: var(--font-heading);
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
}

.addons-mp-subtitle {
  font-family: var(--font-body);
  font-size: 0.875rem;
  color: var(--color-text-muted);
  margin: 0.25rem 0 0;
}

/* ===== Section ===== */
.addons-mp-section {
  animation: addons-mp-card-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.06s both;
}

.addons-mp-section-title {
  font-family: var(--font-heading);
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.addons-mp-section-loading {
  display: flex;
  justify-content: center;
  padding: 1.5rem;
}

/* ===== Grid ===== */
.addons-mp-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.addons-mp-grid--templates {
  grid-template-columns: repeat(3, 1fr);
}

/* ===== Card ===== */
.addons-mp-card {
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  animation: addons-mp-card-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.addons-mp-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.addons-mp-card--large {
  border: 1px solid var(--color-brand-primary);
}

.addons-mp-card--large:hover {
  border-color: var(--color-brand-primary-hover);
}

/* Card header */
.addons-mp-card-header {
  padding: 1rem 1rem 0;
}

.addons-mp-card-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.addons-mp-card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: var(--radius-md);
  background: var(--color-brand-primary);
  color: #fff;
  flex-shrink: 0;
}

.addons-mp-card--large .addons-mp-card-icon {
  width: 34px;
  height: 34px;
}

.addons-mp-card-name {
  font-family: var(--font-heading);
  font-size: 0.925rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.3;
}

.addons-mp-card-desc {
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin: 0;
  line-height: 1.4;
}

/* Card body */
.addons-mp-card-body {
  padding: 0.75rem 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.addons-mp-card-price {
  font-family: var(--font-heading);
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--color-text-primary);
  line-height: 1;
}

.addons-mp-card-pricing-type {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Badges */
.addons-mp-card-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.addons-mp-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: var(--radius-full);
  white-space: nowrap;
}

.addons-mp-badge--active {
  background: var(--color-success-light);
  color: var(--color-success);
}

.addons-mp-badge--trial {
  background: var(--color-warning-light);
  color: var(--color-warning);
}

.addons-mp-badge--trial-offer {
  background: var(--color-info-light);
  color: var(--color-info);
}

/* User count stepper */
.addons-mp-user-stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.65rem;
  background: var(--color-bg-tertiary);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
}

.addons-mp-user-stepper-label {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.addons-mp-user-stepper-controls {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.addons-mp-stepper-btn {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border-default);
  background: var(--color-bg-secondary);
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;
}

.addons-mp-stepper-btn:hover:not(:disabled) {
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  border-color: var(--color-brand-primary);
}

.addons-mp-stepper-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.addons-mp-user-count {
  font-family: var(--font-heading);
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--color-text-primary);
  min-width: 28px;
  text-align: center;
}

.addons-mp-user-total {
  font-family: var(--font-heading);
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-brand-primary);
  white-space: nowrap;
}

/* Tier info */
.addons-mp-card-tier-info {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

/* Card footer */
.addons-mp-card-footer {
  padding: 0.75rem 1rem 1rem;
}

/* ===== Buttons ===== */
.addons-mp-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  font-family: var(--font-body);
  font-size: 0.825rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  padding: 0.5rem 1rem;
  transition: background 0.15s ease, opacity 0.15s ease;
}

.addons-mp-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.addons-mp-btn--purchase {
  background: var(--color-brand-primary);
  color: #fff;
  width: 100%;
}

.addons-mp-btn--purchase:hover:not(:disabled) {
  background: var(--color-brand-primary-hover);
}

.addons-mp-btn--modal {
  width: auto;
}

.addons-mp-btn--cancel {
  background: transparent;
  color: var(--color-text-muted);
  border: 1px solid var(--color-border-default);
  width: 100%;
}

.addons-mp-btn--cancel:hover:not(:disabled) {
  color: var(--color-error);
  border-color: var(--color-error);
}

.addons-mp-btn--ghost {
  background: transparent;
  color: var(--color-text-secondary);
}

.addons-mp-btn--ghost:hover:not(:disabled) {
  background: var(--color-bg-tertiary);
}

.addons-mp-btn--danger {
  background: var(--color-error);
  color: #fff;
}

.addons-mp-btn--danger:hover:not(:disabled) {
  opacity: 0.9;
}

/* ===== Premium Templates Section ===== */
.addons-mp-templates-section {
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  padding: 1.25rem;
}

.addons-mp-templates-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.addons-mp-templates-subtitle {
  font-size: 0.825rem;
  color: var(--color-text-muted);
  margin: 0.15rem 0 0;
}

/* ===== Empty / Loading ===== */
.addons-mp-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

.addons-mp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 1.5rem;
  color: var(--color-text-muted);
  text-align: center;
}

.addons-mp-empty p {
  margin: 0;
  font-size: 0.9rem;
}

/* ===== Modal Overlay ===== */
.addons-mp-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  animation: addons-mp-overlay-in 0.2s ease both;
}

.addons-mp-modal {
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 440px;
  margin: 1rem;
  animation: addons-mp-modal-in 0.25s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.addons-mp-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--color-border-subtle);
}

.addons-mp-modal-header h3 {
  font-family: var(--font-heading);
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.addons-mp-modal-close {
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
}

.addons-mp-modal-close:hover {
  color: var(--color-text-primary);
  background: var(--color-bg-tertiary);
}

.addons-mp-modal-body {
  padding: 1.25rem;
}

.addons-mp-modal-body p {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  line-height: 1.5;
  margin: 0 0 0.75rem;
}

.addons-mp-modal-body p:last-child {
  margin-bottom: 0;
}

.addons-mp-modal-body strong {
  color: var(--color-text-primary);
  font-weight: 600;
}

.addons-mp-modal-trial {
  color: var(--color-info) !important;
  font-size: 0.825rem !important;
}

.addons-mp-modal-warning {
  color: var(--color-error) !important;
}

.addons-mp-modal-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin-bottom: 0.35rem;
}

.addons-mp-modal-textarea {
  width: 100%;
  box-sizing: border-box;
  font-family: var(--font-body);
  font-size: 0.85rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--input-border);
  border-radius: var(--radius-md);
  background: var(--input-bg);
  color: var(--color-text-primary);
  resize: vertical;
}

.addons-mp-modal-textarea:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--input-focus-ring);
  border-color: var(--color-brand-primary);
}

.addons-mp-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem 1rem;
  border-top: 1px solid var(--color-border-subtle);
}

/* ===== Upgrade Modal ===== */
.addons-mp-upgrade-modal {
  max-width: 460px;
  overflow: hidden;
}

.addons-mp-upgrade-accent {
  height: 4px;
  background: linear-gradient(90deg, var(--color-brand-primary), var(--color-warning), var(--color-brand-primary));
  background-size: 200% 100%;
  animation: addons-mp-accent-shimmer 3s ease infinite;
}

.addons-mp-upgrade-header {
  border-bottom: none;
  padding-bottom: 0.5rem;
}

.addons-mp-upgrade-header-left {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.addons-mp-upgrade-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, var(--color-warning), var(--color-brand-primary));
  color: #fff;
  flex-shrink: 0;
}

.addons-mp-upgrade-body {
  padding-top: 0.5rem;
}

.addons-mp-upgrade-message {
  font-size: 0.875rem !important;
  color: var(--color-text-secondary) !important;
  line-height: 1.55 !important;
}

.addons-mp-upgrade-tiers {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--color-bg-tertiary);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border-subtle);
  margin: 0.75rem 0;
}

.addons-mp-upgrade-tier {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
}

.addons-mp-upgrade-tier-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.addons-mp-upgrade-tier-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-family: var(--font-heading);
  font-size: 0.85rem;
  font-weight: 700;
  padding: 0.4rem 0.85rem;
  border-radius: var(--radius-full);
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-default);
  color: var(--color-text-secondary);
}

.addons-mp-upgrade-tier-badge--required {
  background: linear-gradient(135deg, var(--color-warning), var(--color-brand-primary));
  border: none;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.addons-mp-upgrade-tier-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  flex-shrink: 0;
}

.addons-mp-upgrade-hint {
  font-size: 0.8rem !important;
  color: var(--color-text-muted) !important;
  font-style: italic;
  margin-bottom: 0 !important;
}

.addons-mp-btn--upgrade {
  background: linear-gradient(135deg, var(--color-warning), var(--color-brand-primary));
  color: #fff;
  text-decoration: none;
}

.addons-mp-btn--upgrade:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

@keyframes addons-mp-accent-shimmer {
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* ===== Spinner ===== */
.addons-mp-spin {
  animation: addons-mp-spin 0.75s linear infinite;
}

/* ===== Animations ===== */
@keyframes addons-mp-header-in {
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes addons-mp-card-in {
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes addons-mp-overlay-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

@keyframes addons-mp-modal-in {
  from { opacity: 0; transform: scale(0.95) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}

@keyframes addons-mp-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* ===== Responsive ===== */
@media (max-width: 1024px) {
  .addons-mp-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .addons-mp-grid--templates {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .addons-mp-grid,
  .addons-mp-grid--templates {
    grid-template-columns: 1fr;
  }

  .addons-mp-templates-header {
    flex-direction: column;
  }

  .addons-mp-templates-header .addons-mp-btn--purchase {
    width: 100%;
  }

  .addons-mp-modal {
    max-width: 100%;
    margin: 0.5rem;
  }

  .addons-mp-card-price {
    font-size: 1.15rem;
  }
}

/* ===== Reduced Motion ===== */
@media (prefers-reduced-motion: reduce) {
  .addons-mp-header,
  .addons-mp-section,
  .addons-mp-card,
  .addons-mp-overlay,
  .addons-mp-modal {
    animation: none !important;
  }

  .addons-mp-card:hover {
    transform: none;
  }

  .addons-mp-spin {
    animation: addons-mp-spin 1.5s linear infinite !important;
  }
}
`;

export default AddonsMarketplace;
